import React from "react";
import Icon from "./Icon";
import KebabMenu from "../../../components/KebabMenu";
import { checkinAvailability, formatTime, parseDateInfo, sessionSeqLabel } from "./sessionsModel";

const STATUS_TAG = { upcoming: "Upcoming", completed: "Completed" };

function Attendance({ checkedIn, total, live }) {
  const pct = total > 0 ? Math.round((checkedIn / total) * 100) : 0;
  return (
    <div className="att" aria-label={`${checkedIn} of ${total} ${live ? "checked in" : "attended"}`}>
      <div className="top">
        <span><b>{checkedIn}</b> / {total} {live ? "checked in" : "attended"}</span>
        <em>{pct}%</em>
      </div>
      <div className="bar"><i style={{ width: `${checkedIn > 0 ? Math.max(pct, 2) : 0}%` }} /></div>
    </div>
  );
}

function PublishControl({ published, canToggle, onToggle }) {
  if (!canToggle) return <span className={`pub static${published ? "" : " off"}`}>{published ? "Published" : "Not published"}</span>;
  return (
    <button type="button" className="pub" role="switch" aria-checked={published} aria-label="Published to students" onClick={() => onToggle(!published)}>
      <span className="sw" />
      <span className={published ? "" : "off"}>{published ? "Published" : "Not published"}</span>
    </button>
  );
}

/**
 * One session in the list: date, what and where, attendance (or why students can't see it), the publish switch and
 * the row's actions. `children` is the expandable detail (topics, notes, materials) shown under the row when open.
 */
export default function SessionRow({
  session, status, number, title, totalStudents, permissions, expanded, onExpand, onTogglePublished,
  onTakeAttendance, onViewAttendance, onShowQr, onEdit, onDelete, now, children,
}) {
  const { canEdit, canToggleEnabled, canEditAttendance, canShowCheckinQr } = permissions;
  const published = session.enabled === true;
  const live = status === "live";
  const checkedIn = session.checkedIn ?? 0;
  const info = parseDateInfo(session.sessionDate || session.date);
  const virtual = session.sessionType === "VIRTUAL";
  const canTake = canEditAttendance && published && !session.locked;
  const showAttendance = live || status === "completed";
  const qr = checkinAvailability(session, now);
  const showQr = canShowCheckinQr && qr.state !== "unavailable";

  return (
    <article className={`sl-row${published ? "" : " unpub"}`} data-testid={`session-row-${session.id}`}>
      <div className="when">
        <span className="dow">{info?.weekday ?? ""}</span>
        <span className="dt">{info ? `${info.month} ${info.day}` : "—"}</span>
        <span className="seq mono">{sessionSeqLabel(number)}</span>
      </div>

      <div className="main">
        <div className="tags">
          <span><Icon n={virtual ? "monitor" : "pin"} s={13} />{virtual ? "Online" : "In-person"}</span>
          {live && <span className="live"><span className="pulse" aria-hidden="true" />In progress</span>}
          {!published && <span className="hid"><Icon n="eyeOff" s={13} />Hidden from students</span>}
          {!published && <span>{STATUS_TAG[status]}</span>}
        </div>
        <h4 className="title">{title}</h4>
        <div className="meta">
          {(session.startTime || session.endTime) && (
            <span><Icon n="clock" /><span className="t">{formatTime(session.startTime)} – {formatTime(session.endTime)}</span></span>
          )}
          {session.location && <span><Icon n="pin" />{session.location}</span>}
          {session.instructorName && <span><Icon n="user" />{session.instructorName}</span>}
          {session.meetingLink && <span><Icon n="link" />Remote · LMS link</span>}
        </div>
      </div>

      {showAttendance ? <Attendance checkedIn={checkedIn} total={totalStudents} live={live} /> : (
        <div className="unnote">
          <Icon n="eyeOff" />
          <span>
            Students can&apos;t see this session or check in.
            {checkedIn > 0 ? ` ${checkedIn} check-in${checkedIn === 1 ? "" : "s"} recorded so far ${checkedIn === 1 ? "is" : "are"} kept.` : ""}
          </span>
        </div>
      )}

      <div className="pubc"><PublishControl published={published} canToggle={canToggleEnabled} onToggle={onTogglePublished} /></div>

      <div className="acts">
        {showQr && (
          <button type="button" className="btn btn-o" data-testid={`session-qr-${session.id}`}
            title={qr.state === "open" ? undefined : `${qr.reason}. You'll be asked to confirm before showing the code.`}
            onClick={() => onShowQr({ offHours: qr.state !== "open", state: qr.state })}>
            <Icon n="qr" />QR code
          </button>
        )}
        {canTake && <button type="button" className="btn btn-p" onClick={onTakeAttendance}><Icon n="check" w={2.5} />Take attendance</button>}
        {!published && canToggleEnabled && <button type="button" className="btn btn-p" onClick={() => onTogglePublished(true)}>Publish</button>}
        {checkedIn > 0 && <button type="button" className="btn btn-o" onClick={onViewAttendance}>View attendance</button>}
        <button type="button" className={`ib chev${expanded ? " open" : ""}`} aria-expanded={expanded} onClick={onExpand}
          aria-label={expanded ? "Collapse session details" : "Expand session details"}>
          <Icon n="chevDown" s={16} />
        </button>
        {canEdit && (
          <KebabMenu label={`More actions for ${title}`} triggerIcon={<Icon n="more" s={16} />} items={[
            { icon: <Icon n="pencil" s={15} />, label: "Edit session", onSelect: onEdit },
            { icon: <Icon n="trash" s={15} />, label: "Delete session", danger: true, onSelect: onDelete },
          ]} />
        )}
      </div>

      {expanded && <div className="sl-detail">{children}</div>}
    </article>
  );
}
